import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { LeadStatistics } from '../../types';
import { useTheme } from '@mui/material/styles';

const LeadStatisticsPage: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<LeadStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await apiService.getLeadStatistics();
      setStats(data);
    } catch (err: any) {
      console.error('Error fetching lead statistics:', err);
      setError(err.response?.data?.message || 'Failed to load lead statistics');
    } finally {
      setIsLoading(false);
    }
  };

  // Provide a safe default so hooks can run before stats are loaded
  const defaultStats: LeadStatistics = {
    total_leads: 0,
    leads_by_status: {},
    leads_by_source: {},
    conversion_rate: 0,
    average_response_time_hours: null,
    fast_responders_count: 0,
    new_leads: 0, // backend returns `new_leads`
    active_leads: 0, // backend returns `active_leads`
    closed_won: 0,
    closed_lost: 0,
  };

  // Dynamic stat mapping using theme palette
  const statMapping: Record<string, { title: string; icon: React.ReactElement; color: string }> = {
    total_leads: { title: 'Total Leads', icon: <AssignmentIcon />, color: theme.palette.primary.main },
    new_leads: { title: 'New Leads', icon: <TrendingUpIcon />, color: theme.palette.success.main },
    active_leads: { title: 'Active Leads', icon: <TrendingUpIcon />, color: theme.palette.success.dark },
    conversion_rate: { title: 'Conversion Rate', icon: <CheckCircleIcon />, color: theme.palette.success.main },
    average_response_time_hours: { title: 'Avg Response Time', icon: <AccessTimeIcon />, color: theme.palette.warning.main },
    fast_responders_count: { title: 'Fast Responders', icon: <CheckCircleIcon />, color: theme.palette.success.main },
    closed_won: { title: 'Closed Won', icon: <CheckCircleIcon />, color: theme.palette.success.dark },
    closed_lost: { title: 'Closed Lost', icon: <CancelIcon />, color: theme.palette.error.main },
  };

  // Compute cards with a hook unconditionally
  const statCards = useMemo(() => {
    const s = stats ?? defaultStats;

    // Define fallback mapping for keys that differ between endpoints
    const fallbackKey: Record<string, string | null> = {
      total_leads: 'total_leads',
      new_leads: 'new_leads', // admin returns new_leads
      active_leads: 'active_leads',
      conversion_rate: 'conversion_rate',
      average_response_time_hours: 'average_response_time_hours',
      fast_responders_count: 'fast_responders_count',
      closed_won: 'closed_won',
      closed_lost: 'closed_lost',
    };

    return Object.keys(statMapping).map((key) => {
      const map = statMapping[key];

      // Try primary key, then fallback key
      const primaryRaw = (s as any)[key];
      const fallbackKeyVal = (fallbackKey as any)[key];
      const fallbackRaw = typeof fallbackKeyVal === 'string' ? (s as any)[fallbackKeyVal] : undefined;
      let rawValue: any = primaryRaw !== undefined && primaryRaw !== null ? primaryRaw : fallbackRaw;

      let value: any;

      if (key === 'conversion_rate') {
        const convRaw = rawValue;
        if (convRaw === null || convRaw === undefined) {
          value = 'N/A';
        } else {
          const convNum = Number(convRaw) || 0;
          // If backend already returns percentage (e.g., 20.0), show as-is.
          // If backend returns fraction (0.2), multiply by 100.
          const displayNum = convNum > 1 ? convNum : convNum * 100;
          value = `${displayNum.toFixed(1)}%`;
        }
      } else if (key === 'average_response_time_hours') {
        const hrs = rawValue;
        value = hrs !== null && hrs !== undefined ? `${Number(hrs).toFixed(1)}h` : 'N/A';
      } else {
        // Default numeric fallback
        value = rawValue ?? 0;
      }

      return {
        key,
        title: map.title,
        value,
        icon: map.icon,
        color: map.color,
      };
    });
  }, [stats, theme.palette]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Lead Statistics & Analytics
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {statCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.key}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {card.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: card.color,
                      borderRadius: '50%',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {React.isValidElement(card.icon) ? React.cloneElement(card.icon as React.ReactElement<any>, { htmlColor: 'white' }) : card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Leads by Status
              </Typography>
              <Box sx={{ mt: 2 }}>
                {stats.leads_by_status && Object.keys(stats.leads_by_status).length > 0 ? (
                  Object.entries(stats.leads_by_status).map(([status, count]) => (
                    <Box key={status} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {status.replace(/_/g, ' ')}
                      </Typography>
                      <Chip label={count ?? 0} color="primary" size="small" />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Leads by Source
              </Typography>
              <Box sx={{ mt: 2 }}>
                {stats.leads_by_source && Object.keys(stats.leads_by_source).length > 0 ? (
                  Object.entries(stats.leads_by_source).map(([source, count]) => (
                    <Box key={source} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">
                        {sourceDisplayLabels[source] || source.replace(/_/g, ' ')}
                      </Typography>
                      <Chip label={count ?? 0} color="secondary" size="small" />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const sourceDisplayLabels: Record<string, string> = {
  contact_reveal: 'Contact Reveal',
  video_consultation_booking: 'Video Bookings',
  in_person_booking: 'In-Person Bookings',
  phone_booking: 'Phone Bookings',
};

export default LeadStatisticsPage;

